import { createContext, useContext, useState, useCallback, useEffect, useMemo, useRef, type ReactNode } from 'react'
import type { User, UserPermissions } from '@zonasocioscofarte/types'
import { DEMO_CLIENT_NAME, isDemoMode, onDemoModeChange } from '@utils/demoMode'

interface AuthContextType {
  user: User | null
  usuario: string | null
  isAuthenticated: boolean
  isLoading: boolean
  permissions: UserPermissions | null
  login: (kunnr: string, password: string) => Promise<void>
  autologin: (kunnr: string, kunnr2: string, usuario?: string) => Promise<void>
  logout: () => void
  parametroweb: string
  parametrowebds: string
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

function isTokenExpired(token: string): boolean {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]))
    return payload.exp * 1000 < Date.now()
  } catch {
    return true
  }
}

function clearSession() {
  localStorage.removeItem('ecofarte_token')
  localStorage.removeItem('ecofarte_session')
}

const isEmbedded = typeof window !== 'undefined' && window.self !== window.top

const EMBED_HIDDEN_TIMEOUT_MS = 60 * 60 * 1000

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [usuario, setUsuario] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [permissions, setPermissions] = useState<UserPermissions | null>(null)
  const [parametroweb, setParametroweb] = useState('')
  const [parametrowebds, setParametrowebds] = useState('')
  const [demoMode, setDemoMode] = useState(isDemoMode())
  const logoutRef = useRef<() => void>(() => {})

  useEffect(() => onDemoModeChange(() => setDemoMode(isDemoMode())), [])

  const logout = useCallback(() => {
    setUser(null)
    setUsuario(null)
    setPermissions(null)
    setParametroweb('')
    setParametrowebds('')
    clearSession()
  }, [])

  logoutRef.current = logout

  useEffect(() => {
    const token = localStorage.getItem('ecofarte_token')
    const session = localStorage.getItem('ecofarte_session')

    if (token && session) {
      if (isTokenExpired(token)) {
        clearSession()
        setIsLoading(false)
        return
      }
      try {
        const data = JSON.parse(session)
        setUser(data.user)
        setUsuario(data.usuario || null)
        setPermissions(data.permissions)
        setParametroweb(data.parametroweb || '')
        setParametrowebds(data.parametrowebds || '')
      } catch {
        clearSession()
      }
    }
    setIsLoading(false)
  }, [])

  useEffect(() => {
    if (!isEmbedded) return

    const purge = (e: Event) => {
      if ('persisted' in e && (e as PageTransitionEvent).persisted) return
      clearSession()
    }

    const opts: AddEventListenerOptions = { capture: true }
    window.addEventListener('pagehide', purge, opts)
    window.addEventListener('beforeunload', purge, opts)
    return () => {
      window.removeEventListener('pagehide', purge, opts)
      window.removeEventListener('beforeunload', purge, opts)
    }
  }, [])

  useEffect(() => {
    if (!isEmbedded) return

    let hiddenTimer: number | undefined

    const onVisibility = () => {
      if (document.hidden) {
        window.clearTimeout(hiddenTimer)
        hiddenTimer = window.setTimeout(() => {
          logoutRef.current()
          window.location.reload()
        }, EMBED_HIDDEN_TIMEOUT_MS)
      } else {
        window.clearTimeout(hiddenTimer)
      }
    }

    document.addEventListener('visibilitychange', onVisibility)
    return () => {
      window.clearTimeout(hiddenTimer)
      document.removeEventListener('visibilitychange', onVisibility)
    }
  }, [])

  useEffect(() => {
    const originalFetch = window.fetch

    window.fetch = async (input, init) => {
      const response = await originalFetch(input, init)

      if (response.status === 401 || response.status === 403) {
        const cloned = response.clone()
        try {
          const body = await cloned.json()
          const code = body?.error?.code || ''
          if (code === 'INVALID_TOKEN' || code === 'AUTH_REQUIRED' || code === 'TOKEN_EXPIRED') {
            clearSession()
            logoutRef.current()
            window.location.href = '/login'
            return new Response(JSON.stringify(body), {
              status: response.status,
              headers: response.headers
            })
          }
        } catch {
          // ignore parse errors (non-JSON responses)
        }
      }

      return response
    }

    return () => {
      window.fetch = originalFetch
    }
  }, [])

  const persistSession = useCallback((data: {
    token: string
    user: User
    usuario?: string
    permissions?: UserPermissions
    parametroweb?: string
    parametrowebds?: string
  }) => {
    setUser(data.user)
    setUsuario(data.usuario || null)
    setPermissions(data.permissions || null)
    setParametroweb(data.parametroweb || '')
    setParametrowebds(data.parametrowebds || '')

    localStorage.setItem('ecofarte_token', data.token)
    localStorage.setItem('ecofarte_session', JSON.stringify({
      token: data.token,
      user: data.user,
      usuario: data.usuario,
      permissions: data.permissions,
      parametroweb: data.parametroweb,
      parametrowebds: data.parametrowebds
    }))
  }, [])

  const extractErrorMessage = useCallback(async (response: Response): Promise<string> => {
    let errorMessage = 'Error de autenticación'
    try {
      const error = await response.json()
      errorMessage = error.error?.message || error.message || errorMessage
    } catch {
      errorMessage = await response.text().catch(() => errorMessage)
    }
    return errorMessage
  }, [])

  const login = useCallback(async (kunnr: string, password: string) => {
    setIsLoading(true)
    try {
      setUser(null)
      setUsuario(null)
      setPermissions(null)
      setParametroweb('')
      setParametrowebds('')
      clearSession()

      const response = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ kunnr, password })
      })

      if (!response.ok) {
        throw new Error(await extractErrorMessage(response))
      }

      const data = await response.json()
      persistSession(data)
    } finally {
      setIsLoading(false)
    }
  }, [extractErrorMessage, persistSession])

  const autologin = useCallback(async (kunnr: string, kunnr2: string, usuario?: string) => {
    setIsLoading(true)
    try {
      setUser(null)
      setUsuario(null)
      setPermissions(null)
      setParametroweb('')
      setParametrowebds('')
      clearSession()

      const response = await fetch('/api/auth/autologin', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ kunnr, kunnr2, usuario })
      })

      if (!response.ok) {
        throw new Error(await extractErrorMessage(response))
      }

      const data = await response.json()
      persistSession(data)
    } finally {
      setIsLoading(false)
    }
  }, [extractErrorMessage, persistSession])

  const exposedUser = useMemo(() => {
    if (!user) return null
    if (!demoMode) return user
    return {
      ...user,
      name1: DEMO_CLIENT_NAME,
      name2: '',
      stras: '',
      ort01: '',
      pstlz: '',
      telf1: '',
      smtp_addr: ''
    }
  }, [user, demoMode])

  return (
    <AuthContext.Provider value={{
      user: exposedUser,
      usuario,
      isAuthenticated: !!user,
      isLoading,
      permissions,
      login,
      autologin,
      logout,
      parametroweb,
      parametrowebds
    }}>
      {children}
    </AuthContext.Provider>
  )
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
